/**
 * C-GTCC-VIEW-OVW1 — typed read client for the existing risk-radar module.
 *
 * Unlike `./ops.ts` and `./svc.ts` this route is **not** a future contract: it
 * exists today. `/v1/admin/ai/risk-radar` is the endpoint the old console view
 * behind registry key `risk` reads
 * (`apps/admin/src/gtcc/views/risk/GtccRiskView.tsx:73`), and the old
 * Operations Overview reads it as well
 * (`apps/admin/src/gtcc/GtccOverviewView.tsx:201`). The instruction asks for
 * "the existing risk-radar module of the API (registry key `risk` of the old
 * console, endpoint to be measured)" — this is that measurement, transcribed
 * from the shape both call sites already declare.
 *
 * It contributes tenant identity, the module's own risk level and its signal
 * texts. The per-tenant counts of the Risk Radar table (vehicles, silent
 * devices, alerts, data confidence) come from `ops/vehicle-flow`; this module
 * never carries them.
 */
import { gtccGet, type GtccResult } from "./gtcc-result.js";

/* --------------------------------------------------------------- paths */

export const RISK_RADAR_PATH = "/v1/admin/ai/risk-radar";

/* ---------------------------------------------------------------- types */

/** The level enum of the existing module — not the GTCC status enum. */
export const RISK_LEVELS = ["ok", "watch", "warning", "critical", "unknown"] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];

export interface RiskSignalDto {
  readonly status: RiskLevel;
  readonly explanation: string;
}

export interface RiskTenantDto {
  readonly tenantId: string;
  readonly displayName: string | null;
  readonly riskLevel: RiskLevel;
  readonly riskScore: number;
  readonly signals: ReadonlyArray<RiskSignalDto>;
  readonly dataGaps: ReadonlyArray<string>;
}

export interface RiskRadarSummaryDto {
  readonly byLevel: Record<RiskLevel, number>;
  readonly totalTenants: number;
  readonly instrumentedTenants: number;
  readonly notInstrumentedTenants: number;
}

export interface RiskRadarDto {
  readonly radar: {
    readonly tenants: ReadonlyArray<RiskTenantDto>;
    readonly summary: RiskRadarSummaryDto;
  };
}

/* -------------------------------------------------------------- fetchers */

export function fetchRiskRadar(signal?: AbortSignal): Promise<GtccResult<RiskRadarDto>> {
  return gtccGet<RiskRadarDto>(RISK_RADAR_PATH, signal);
}

/* ----------------------------------------------------------- query keys */

/*
 * Own array literals for the reason documented in `./ops.ts`. The keys are
 * distinct from the old view's `["gtcc", "risk", "radar"]` so both views can
 * exist side by side until D-GTCC-SHELL1 is merged and the old files are
 * removed; the result shape differs (`GtccResult` vs. a thrown `ApiError`),
 * so sharing one cache entry would hand one view the other's payload.
 */
export const RISK_TENANTS_KEY = ["gtcc", "risk", "tenant-risk"] as const;
export const RISK_TENANTS_OVERVIEW_KEY = ["gtcc", "overview", "tenant-risk"] as const;

export const RISK_QUERY_KEYS = {
  tenants: RISK_TENANTS_KEY,
  tenantsForOverview: RISK_TENANTS_OVERVIEW_KEY,
} as const;
